import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch, getIngestUrl } from '../config';
import { ArrowLeft, MagnifyingGlass, MapPin, ArrowUUpLeft, CheckCircle, Export } from '@phosphor-icons/react';

interface StagingRow {
  id: number;
  contact_id: string;
  orig_vnm: string | null; orig_nnm: string | null; orig_str: string | null; orig_plz: string | null; orig_ort: string | null;
  clean_vnm: string | null; clean_nnm: string | null; clean_str: string | null; clean_hnr: string | null; abuntis_hna: string | null; clean_plz: string | null; clean_ort: string | null;
  titel: string | null; geburtsdatum: string | null;
  email: string | null; telefon: string | null; handy: string | null;
  email_current: string | null; telefon_current: string | null; handy_current: string | null;
  oc_email: string | null; oc_telefon: string | null; oc_handy: string | null;
  adresse_ok: number; strasse_bad: number; umgezogen: number;
  status: string;
}

interface ReviewData {
  rows: StagingRow[];
  total: number;
  totalAll: number;
  counts: { auffaellig: number; unauffaellig: number; bearbeitet: number };
  subcounts: Record<string, number>;
  page: number;
  totalPages: number;
  filename: string;
  importedAt: string | null;
}

const FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'adresse', label: 'Adresse NOK' },
  { key: 'adresse_abweichung', label: 'Levenshtein' },
  { key: 'hna', label: 'Hausnummer' },
  { key: 'titel', label: 'Titel' },
  { key: 'geburtsdatum', label: 'Geburtsdatum' },
  { key: 'str_abgleich', label: 'Straße abweichend' },
  { key: 'bearbeitet', label: 'Bearbeitet' },
] as const;

function getGrund(row: StagingRow): string {
  const gruende: string[] = [];
  if (row.umgezogen === 1) gruende.push('Umgezogen');
  if (row.strasse_bad === 1) gruende.push('Straße ungültig');
  if (row.adresse_ok !== 1) gruende.push('Adresse nicht OK');
  if (row.titel && !['Dr.', 'Prof.', 'Prof. Dr.', 'Dipl. Ing.'].includes(row.titel)) {
    gruende.push(`Titel: ${row.titel}`);
  }
  const vnm = row.clean_vnm || '';
  const nnm = row.clean_nnm || '';
  if (/^(Dr[. ]|Prof[. ]|Dipl[. ])/i.test(vnm) || /^(Dr[. ]|Prof[. ]|Dipl[. ])/i.test(nnm)) {
    gruende.push(`Titel im Namen`);
  }
  if (row.geburtsdatum && (
    !/^\d{4}-\d{2}-\d{2}$/.test(row.geburtsdatum) ||
    row.geburtsdatum < '1925-01-01' || row.geburtsdatum > '2008-01-01'
  )) {
    gruende.push(`Geb: ${row.geburtsdatum}`);
  }
  if (row.abuntis_hna && (/^\d/.test(row.abuntis_hna) || row.abuntis_hna.length > 1)) {
    gruende.push(`HNr-Erw: ${row.abuntis_hna}`);
  }
  return gruende.join(', ') || '';
}

function mapsUrl(str: string | null, plz: string | null, ort: string | null): string {
  const parts = [str, plz, ort].filter(Boolean).join(' ');
  return `https://www.google.com/maps/search/${encodeURIComponent(parts)}`;
}

function isDiff(orig: string | null, clean: string | null): boolean {
  return (orig || '') !== (clean || '');
}

// Geburtsdatum gilt nur als 'bad', wenn Format kaputt, zu alt (<1925) oder zu jung (>2008).
// Leer = ok (kein Datum -> nichts hervorheben).
function isBadGeb(value: string | null | undefined): boolean {
  const v = (value || '').trim();
  if (!v) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return true;
  return v < '1925-01-01' || v > '2008-01-01';
}

const diffStyle: React.CSSProperties = {
  background: '#fef2f2', color: '#dc2626', padding: '1px 4px', borderRadius: '3px',
  border: '1px solid #fecaca', fontWeight: 600,
};
const sameStyle: React.CSSProperties = { color: '#9ca3af' };

export default function AbuntisReview() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ReviewData | null>(null);
  const [activeFilter, setActiveFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (searchDebounced) params.set('search', searchDebounced);
      if (activeFilter) params.set('filter', activeFilter);
      const res = await authFetch(getIngestUrl(`/abuntis-review/${jobId}?${params}`));
      const json = await res.json();
      if (json.success) setData(json);
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  }, [jobId, page, searchDebounced, activeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRevert = async (id: number) => {
    setRevertingId(id);
    try {
      await authFetch(getIngestUrl(`/abuntis-review/${jobId}/records/${id}/revert`), { method: 'POST' });
      await fetchData();
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`);
    }
    setRevertingId(null);
  };

  const handleSaveEdit = async (id: number) => {
    try {
      await authFetch(getIngestUrl(`/abuntis-review/${jobId}/records/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues),
      });
      setExpandedId(null);
      setEditValues({});
      await fetchData();
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`);
    }
  };

  const handleImport = async () => {
    if (!window.confirm('Alle Datensätze jetzt in optin_cache importieren?')) return;
    setImporting(true);
    try {
      const res = await authFetch(getIngestUrl(`/abuntis-review/${jobId}/import`), { method: 'POST' });
      const json = await res.json();
      if (json.success) {
        alert(`Import erfolgreich: ${json.updated} aktualisiert, ${json.unchanged} unverändert, ${json.notFound} nicht gefunden`);
        navigate('/dialfire');
      } else {
        alert(`Import-Fehler: ${json.error || json.message}`);
      }
    } catch (err) {
      alert(`Import fehlgeschlagen: ${(err as Error).message}`);
    }
    setImporting(false);
  };

  const openDetail = (row: StagingRow) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      setEditValues({});
    } else {
      setExpandedId(row.id);
      setEditValues({
        clean_vnm: row.clean_vnm || '',
        clean_nnm: row.clean_nnm || '',
        clean_str: row.clean_str || '',
        clean_hnr: row.clean_hnr || '',
        abuntis_hna: row.abuntis_hna || '',
        clean_plz: row.clean_plz || '',
        clean_ort: row.clean_ort || '',
        titel: row.titel || '',
        geburtsdatum: row.geburtsdatum || '',
        email: row.email_current || '',
        telefon: row.telefon_current || '',
        handy: row.handy_current || '',
      });
    }
  };

  const isImported = data?.importedAt != null;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1rem' }}>
      <button onClick={() => navigate('/dialfire')} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: '#003366',
        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', marginBottom: '0.5rem', padding: 0,
      }}>
        <ArrowLeft size={16} /> Zurück zu Dialfire Sync
      </button>

      <div style={{
        background: '#003366', color: 'white', padding: '12px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderRadius: '8px 8px 0 0',
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
          Abuntis Review — {data?.filename || '...'}
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
            {data ? `${data.totalAll.toLocaleString('de-DE')} Datensätze` : '...'}
          </span>
          {isImported && (
            <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 500 }}>Bereits importiert</span>
          )}
          <button
            onClick={handleImport}
            disabled={importing}
            style={{
              padding: '8px 16px', background: '#059669', color: 'white',
              border: 'none', borderRadius: '6px', cursor: importing ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <Export size={18} weight="bold" />
            {importing ? 'Importiere...' : 'Import durchführen'}
          </button>
        </div>
      </div>

      {/* Suche */}
      <div style={{
        display: 'flex', gap: '10px', alignItems: 'center',
        padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ position: 'relative' }}>
          <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: 8, color: '#9ca3af' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suche nach Name, PLZ, Ort, Straße, Contact-ID..."
            style={{
              padding: '6px 12px 6px 32px', border: '1px solid #d1d5db', borderRadius: '6px',
              width: '350px', fontSize: '0.85rem',
            }}
          />
        </div>
        {data && (
          <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
            {data.total.toLocaleString('de-DE')} von {data.totalAll.toLocaleString('de-DE')}
          </span>
        )}
      </div>

      {/* Filter */}
      <div style={{
        display: 'flex', gap: '12px', padding: '8px 16px', alignItems: 'center', flexWrap: 'wrap',
        background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', fontSize: '0.82rem',
      }}>
        {FILTERS.map(f => {
          const count = f.key === '' ? data?.totalAll ?? 0 : (data?.subcounts?.[f.key] ?? 0);
          return (
            <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#374151' }}>
              <input
                type="radio"
                name="review-filter"
                checked={activeFilter === f.key}
                onChange={() => { setActiveFilter(f.key); setPage(1); }}
                style={{ accentColor: '#003366' }}
              />
              {f.label}
              <span style={{
                background: activeFilter === f.key ? '#003366' : '#e5e7eb',
                color: activeFilter === f.key ? 'white' : '#6b7280',
                borderRadius: '10px', padding: '0 6px', fontSize: '0.72rem',
              }}>
                {count.toLocaleString('de-DE')}
              </span>
            </label>
          );
        })}
      </div>

      <div style={{ overflowX: 'auto', background: 'white' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Lade...</div>
        ) : data && data.rows.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                <th style={thStyle}></th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Straße</th>
                <th style={thStyle}>PLZ</th>
                <th style={thStyle}>Ort</th>
                <th style={thStyle}>Maps</th>
                <th style={thStyle}>Adressstatus</th>
                <th style={thStyle}>Grund</th>
                <th style={thStyle}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => {
                const nameDiff = isDiff(row.orig_vnm, row.clean_vnm) || isDiff(row.orig_nnm, row.clean_nnm);
                const strDiff = isDiff(row.orig_str, (row.clean_str || '') + (row.clean_hnr ? ' ' + row.clean_hnr : ''));
                const plzDiff = isDiff(row.orig_plz, row.clean_plz);
                const ortDiff = isDiff(row.orig_ort, row.clean_ort);
                const grund = getGrund(row);
                const isExpanded = expandedId === row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr onClick={() => openDetail(row)} style={{ cursor: 'pointer' }}>
                      <td rowSpan={2} style={{ ...tdStyle, verticalAlign: 'middle', width: 24 }}>
                        {row.status === 'bearbeitet' ? (
                          <CheckCircle size={16} color="#2563eb" weight="fill" />
                        ) : row.status === 'auffaellig' ? (
                          <span style={{ color: '#f59e0b', fontSize: 16 }}>⚠</span>
                        ) : null}
                      </td>
                      <td style={tdStyle}><span style={{ ...labelTag, ...labelAlt }}>Alt</span></td>
                      <td style={tdStyle}>
                        <span style={nameDiff ? {} : sameStyle}>{[row.orig_vnm, row.orig_nnm].filter(Boolean).join(' ')}</span>
                      </td>
                      <td style={tdStyle}><span style={strDiff ? {} : sameStyle}>{row.orig_str || ''}</span></td>
                      <td style={tdStyle}><span style={plzDiff ? {} : sameStyle}>{row.orig_plz || ''}</span></td>
                      <td style={tdStyle}><span style={ortDiff ? {} : sameStyle}>{row.orig_ort || ''}</span></td>
                      <td style={tdStyle}>
                        <a href={mapsUrl(row.orig_str, row.orig_plz, row.orig_ort)} target="_blank" rel="noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.75rem' }}>
                          <MapPin size={12} /> Maps
                        </a>
                      </td>
                      <td rowSpan={2} style={{ ...tdStyle, verticalAlign: 'middle', fontSize: '0.7rem' }}>
                        {(row as any).address_state && (
                          <span style={{
                            display: 'inline-block', padding: '1px 5px', borderRadius: '3px', fontWeight: 600, fontSize: '0.65rem',
                            background: (row as any).address_state === '$valid' ? '#dcfce7' : (row as any).address_state === '$invalid' ? '#fee2e2' : (row as any).address_state === '$forced' ? '#fff7ed' : '#f3f4f6',
                            color: (row as any).address_state === '$valid' ? '#166534' : (row as any).address_state === '$invalid' ? '#991b1b' : (row as any).address_state === '$forced' ? '#c2410c' : '#6b7280',
                          }}>{(row as any).address_state}</span>
                        )}
                        {(row as any).address_update === '1' && (
                          <span style={{ display: 'block', marginTop: 2, fontSize: '0.65rem', color: '#2563eb' }}>Aktualisiert</span>
                        )}
                        <div style={{ marginTop: 2, color: '#9ca3af', fontSize: '0.63rem', fontFamily: 'monospace' }}>{row.contact_id}</div>
                      </td>
                      <td rowSpan={2} style={{ ...tdStyle, verticalAlign: 'middle' }}>
                        {grund && <span style={{
                          fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px', fontWeight: 500,
                          background: row.umgezogen ? '#fffbeb' : '#fef2f2',
                          color: row.umgezogen ? '#d97706' : '#dc2626',
                          border: `1px solid ${row.umgezogen ? '#fde68a' : '#fecaca'}`,
                        }}>{grund}</span>}
                      </td>
                      <td rowSpan={2} style={{ ...tdStyle, verticalAlign: 'middle' }}>
                          <button
                            onClick={e => { e.stopPropagation(); handleRevert(row.id); }}
                            disabled={revertingId === row.id}
                            style={{
                              padding: '3px 8px', background: 'white', border: '1px solid #d1d5db',
                              borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#374151',
                              display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap',
                            }}
                          >
                            <ArrowUUpLeft size={12} /> Original
                          </button>
                      </td>
                    </tr>
                    <tr onClick={() => openDetail(row)} style={{ cursor: 'pointer', borderBottom: '2px solid #e5e7eb' }}>
                      <td style={tdStyle}><span style={{ ...labelTag, ...labelClean }}>Clean</span></td>
                      <td style={tdStyle}>
                        <span style={nameDiff ? diffStyle : sameStyle}>
                          {[row.clean_vnm, row.clean_nnm].filter(Boolean).join(' ')}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={strDiff ? diffStyle : sameStyle}>
                          {[row.clean_str, row.clean_hnr].filter(Boolean).join(' ')}
                        </span>
                      </td>
                      <td style={tdStyle}><span style={plzDiff ? diffStyle : sameStyle}>{row.clean_plz || ''}</span></td>
                      <td style={tdStyle}><span style={ortDiff ? diffStyle : sameStyle}>{row.clean_ort || ''}</span></td>
                      <td style={tdStyle}>
                        <a href={mapsUrl((row.clean_str || '') + (row.clean_hnr ? ' ' + row.clean_hnr : ''), row.clean_plz, row.clean_ort)}
                          target="_blank" rel="noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.75rem' }}>
                          <MapPin size={12} /> Maps
                        </a>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} style={{ padding: 0 }}>
                          <div style={{ border: '2px solid #003366', borderRadius: '0 0 8px 8px' }}>
                            <div style={{
                              background: '#003366', color: 'white', padding: '8px 16px', fontSize: '0.8rem',
                              display: 'flex', justifyContent: 'space-between',
                            }}>
                              <span>Detail: <strong>{row.orig_vnm} {row.orig_nnm}</strong> ({row.contact_id})</span>
                            </div>
                            <div style={{
                              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
                              padding: '16px', background: '#f9fafb',
                            }}>
                              <div>
                                <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>Original-Daten</h4>
                                {[
                                  ['Vorname', row.orig_vnm], ['Nachname', row.orig_nnm],
                                  ['Titel', row.titel],
                                  ['Straße', row.orig_str], ['PLZ', row.orig_plz], ['Ort', row.orig_ort],
                                  ['Email', row.oc_email], ['Telefon', row.oc_telefon], ['Handy', row.oc_handy],
                                ].map(([label, val]) => (
                                  <div key={label as string} style={{ display: 'flex', padding: '4px 0', fontSize: '0.8rem' }}>
                                    <label style={{ color: '#6b7280', width: 70, fontSize: '0.75rem' }}>{label}</label>
                                    <span>{val || ''}</span>
                                  </div>
                                ))}
                                <a href={mapsUrl(row.orig_str, row.orig_plz, row.orig_ort)} target="_blank" rel="noreferrer"
                                  style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.8rem', marginTop: 8, display: 'inline-block' }}>
                                  <MapPin size={14} /> Original in Google Maps
                                </a>
                              </div>
                              <div>
                                <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>Clean-Daten — editierbar</h4>
                                {[
                                  ['Vorname', 'clean_vnm', row.orig_vnm],
                                  ['Nachname', 'clean_nnm', row.orig_nnm],
                                  ['Titel', 'titel', ''],
                                  ['Geb.datum', 'geburtsdatum', ''],
                                  ['Straße', 'clean_str', row.orig_str],
                                  ['Hausnr.', 'clean_hnr', ''],
                                  ['HNr-Erw.', 'abuntis_hna', ''],
                                  ['PLZ', 'clean_plz', row.orig_plz],
                                  ['Ort', 'clean_ort', row.orig_ort],
                                  ['Email', 'email', row.oc_email],
                                  ['Telefon', 'telefon', row.oc_telefon],
                                  ['Handy', 'handy', row.oc_handy],
                                ].map(([label, field, origVal]) => {
                                  // Geburtsdatum: rot nur bei fehlerhaftem Format / zu alt / zu jung,
                                  // sonst (auch bei reiner Änderung) neutral.
                                  const isBad = field === 'geburtsdatum'
                                    ? isBadGeb(editValues[field as string])
                                    : isDiff(origVal as string, editValues[field as string]);
                                  return (
                                  <div key={field as string} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '0.8rem' }}>
                                    <label style={{ color: '#6b7280', width: 60, fontSize: '0.75rem' }}>{label}</label>
                                    <input
                                      value={editValues[field as string] || ''}
                                      onChange={e => setEditValues(v => ({ ...v, [field as string]: e.target.value }))}
                                      style={{
                                        padding: '4px 8px', border: '1px solid', borderRadius: 4, fontSize: '0.8rem', flex: 1,
                                        borderColor: isBad ? '#dc2626' : '#d1d5db',
                                        background: isBad ? '#fef2f2' : 'white',
                                      }}
                                    />
                                    {origVal && (
                                      <button
                                        onClick={() => setEditValues(v => {
                                          const updated = { ...v, [field as string]: origVal as string };
                                          if (field === 'clean_str') updated.clean_hnr = '';
                                          return updated;
                                        })}
                                        style={{
                                          background: 'none', border: '1px solid #d1d5db', borderRadius: 4,
                                          padding: '2px 6px', cursor: 'pointer', fontSize: '0.7rem', color: '#6b7280',
                                        }}
                                        title="Original übernehmen"
                                      >↩</button>
                                    )}
                                  </div>
                                  );
                                })}
                                <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                                  <a href={mapsUrl(
                                    (editValues.clean_str || '') + (editValues.clean_hnr ? ' ' + editValues.clean_hnr : ''),
                                    editValues.clean_plz, editValues.clean_ort
                                  )} target="_blank" rel="noreferrer"
                                    style={{ color: '#2563eb', textDecoration: 'none', fontSize: '0.8rem' }}>
                                    <MapPin size={14} /> Clean in Google Maps
                                  </a>
                                  <button onClick={() => handleSaveEdit(row.id)} style={{
                                    padding: '4px 12px', background: '#003366', color: 'white',
                                    border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem',
                                    display: 'flex', alignItems: 'center', gap: 4,
                                  }}>
                                    <CheckCircle size={14} /> Als bearbeitet markieren
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
            Keine Datensätze in diesem Tab
          </div>
        )}
      </div>

      {/* Paginierung */}
      {data && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center', padding: '12px 16px', fontSize: '0.85rem', color: '#6b7280' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>←</button>
          <span>Seite {data.page} / {data.totalPages || 1}</span>
          <button disabled={page >= (data.totalPages || 1)} onClick={() => setPage(p => p + 1)}
            style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: page >= (data.totalPages || 1) ? 'not-allowed' : 'pointer' }}>→</button>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  background: '#f9fafb', padding: '8px 10px', textAlign: 'left', fontSize: '0.7rem',
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = { padding: '4px 10px', fontSize: '0.8rem', verticalAlign: 'top' };
const labelTag: React.CSSProperties = {
  display: 'inline-block', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.5px', padding: '1px 6px', borderRadius: 3, width: 38, textAlign: 'center',
};
const labelAlt: React.CSSProperties = { background: '#f3f4f6', color: '#6b7280' };
const labelClean: React.CSSProperties = { background: '#dbeafe', color: '#1d4ed8' };
